import { Avatar, Tag, Tooltip, Button, Popconfirm, Progress } from 'antd';
import { EditFilled, DeleteFilled, ReloadOutlined, CalendarOutlined, UserOutlined, CopyOutlined } from '@ant-design/icons';
import Moment from 'react-moment';
import { useEffect } from 'react';
import { useSelector } from '@legendapp/state/react';
import { state$ } from '../../state/globalState';
import { directoriesState } from '../../state/actions/directoriesState';
import { affairesState } from '../../state/actions/affairesState';
import { AffaireService } from '../../services/AffaireService';
import { catchError } from '../../services/DaoService';
import { copyLinkToClipboard } from '../../helpers/clipboard';
import { InlineEditableInput } from '../utils/InlineEditableInput';
import AiSummarySection from '../ai/AiSummarySection';
import styles from './AffaireHero.module.scss';
import PropTypes from 'prop-types';

export default function AffaireHero({ affaire, isReadOnly, loading, onEdit, onDelete, onRefresh, canManage }) {
  const users = useSelector(() => state$.usersDirectory.list.get());

  useEffect(() => { directoriesState.loadUsers(); }, []);

  const handleFieldChange = (value, fieldName, record) => {
    AffaireService.getInstance()
      .update(record.uid, { ...record, [fieldName]: value })
      .then(r => affairesState.updateAffaireInList(r.data))
      .catch(catchError);
  };

  const handleCopyLink = () => copyLinkToClipboard(`/affaire/${affaire.uid}`, 'Lien du dossier');

  return (
    <div className={styles.hero}>
      <div className={styles.heroBand} style={{ background: affaire.priorite?.couleur ?? '#3E65AC' }} />
      <div className={styles.heroContent}>
        <div className={styles.heroLeft}>
          <div className={styles.heroMeta}>
            <Tooltip title="Copier le lien du dossier">
              <span className={styles.heroCode} onClick={handleCopyLink} style={{ cursor: 'pointer' }}>
                {affaire.code} <CopyOutlined style={{ fontSize: 11 }} />
              </span>
            </Tooltip>
            {affaire.modele?.nom && <Tag color={affaire.modele?.couleur ?? 'default'} className={styles.heroTag}>{affaire.modele.nom}</Tag>}
            {affaire.priorite?.nom && <Tag color={affaire.priorite?.couleur ?? 'default'} className={styles.heroTag}>{affaire.priorite.nom}</Tag>}
            {!!affaire.done && <Tag color="default" className={styles.heroTag}>Clôturé</Tag>}
            {!!affaire.retard && !affaire.done && <Tag color="red" className={styles.heroTag}>En retard</Tag>}
          </div>
          <h1 className={styles.heroTitle}>
            <InlineEditableInput
              canEdit={canManage && !isReadOnly}
              type="text"
              fieldName="nom"
              value={affaire.nom}
              record={affaire}
              onChange={handleFieldChange}
            >
              {affaire.nom ?? '—'}
            </InlineEditableInput>
          </h1>
          <div className={styles.heroInfo}>
            <span className={styles.heroInfoItem}>
              <UserOutlined className={styles.heroInfoIcon} />
              <InlineEditableInput
                canEdit={canManage && !isReadOnly}
                type="select"
                fieldName="responsable_id"
                value={affaire.responsable_id}
                record={affaire}
                onChange={handleFieldChange}
                optionFilterProp="full_name"
                options={users.map(u => ({
                  value: u.id,
                  label: (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Avatar size={20} src={u.avatar}>{u.name?.[0]}</Avatar>
                      {u.name}
                    </span>
                  ),
                  full_name: u.name,
                }))}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Avatar size={20} src={affaire.responsable?.avatar}>{affaire.responsable?.name?.[0]}</Avatar>
                  <span>{affaire.responsable?.name ?? 'Sans responsable'}</span>
                </span>
              </InlineEditableInput>
            </span>
            {affaire.date_limite && (
              <span className={styles.heroInfoItem}>
                <CalendarOutlined className={styles.heroInfoIcon} />
                <Moment format="DD MMM YYYY">{affaire.date_limite}</Moment>
              </span>
            )}
            {affaire.progression != null && (
              <span className={`${styles.heroInfoItem} ${styles.heroInfoProgression}`}>
                {affaire.progression}% complété
              </span>
            )}
          </div>
          {affaire.progression != null && (
            <Progress
              percent={affaire.progression}
              size={['100%', 4]}
              showInfo={false}
              className={styles.heroProgress}
              strokeColor={affaire.priorite?.couleur ?? '#3E65AC'}
            />
          )}
        </div>
        <div className={styles.heroActions}>
          <Tooltip title="Rafraîchir">
            <Button type="text" icon={<ReloadOutlined />} loading={loading} onClick={onRefresh} />
          </Tooltip>
          {canManage && !isReadOnly && (
            <>
              <Button type="primary" icon={<EditFilled />} onClick={onEdit}>Éditer</Button>
              <Popconfirm
                title="Supprimer ce dossier ?"
                onConfirm={onDelete}
                okText="Supprimer"
                okButtonProps={{ danger: true }}
                cancelText="Annuler"
              >
                <Button danger icon={<DeleteFilled />}>Supprimer</Button>
              </Popconfirm>
            </>
          )}
        </div>
      </div>
      <div className={styles.heroSummary}>
        <AiSummarySection type="affaire" uid={affaire.uid} />
      </div>
    </div>
  );
}

AffaireHero.propTypes = {
  affaire: PropTypes.object.isRequired,
  isReadOnly: PropTypes.bool,
  loading: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onRefresh: PropTypes.func,
  canManage: PropTypes.bool,
};
